// client/src/App.js
import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Home from "./components/Home";
import About from "./components/About";
import AboutEMDR from "./components/AboutEMDR";
import WhatIsBilateralStimulation from "./components/WhatIsBilateralStimulation";
import Contact from "./components/Contact";
import Confirmation from "./components/Confirmation";
import ResetPassword from "./components/ResetPassword";
import AccountSetup from "./components/AccountSetup";
import Subscribe from "./components/Subscribe";
import SessionSelection from "./components/SessionSelection";
import UnguidedSession from "./components/UnguidedSession";
import ARTSession from "./components/ARTSession";
import PublicDemo from "./components/PublicDemo";
import PublicTrySession from "./components/PublicTrySession";
import Sidebar from "./components/Sidebar";
import DotContext from "./contexts/DotContext";
import { SessionThemeProvider, useSessionTheme } from "./contexts/SessionThemeContext";
import SessionHistory from "./components/SessionHistory";
import SignInSignUp from "./components/SignInSignUp";
import SessionEnd from "./components/SessionEnd";
import ErrorBoundary from "./components/ErrorBoundary";
import UserProfile from "./components/UserProfile";
import TermsOfService from "./components/TermsOfService";
import Disclaimer from "./components/Disclaimer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ReturnPolicy from "./components/Refunds";
import CrisisPage from "./components/CrisisPage";
import CookieConsentBar from "./components/CookieConsentBar";
import LoopsExitPopup from "./components/LoopsExitPopup";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import ForgotPassword from "./components/ForgotPassword";
import TechnicalStack from "./components/TechnicalStack";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import LoadingSkeleton from "./components/LoadingSkeleton";
import LandingPageA from "./components/LandingPageA";
import LandingPageB from "./components/LandingPageB";
import StressLandingPage from "./components/StressLandingPage";
import BalanceLandingPage from "./components/BalanceLandingPage";
import { DEFAULT_SOUND_FILE } from "./utils/soundOptions";

// Session theme → resolved dot / background hex values used by DotMovement.
// Mirrors the CSS-variable definitions in App.css so the dot can read these
// without round-tripping through getComputedStyle.
const SESSION_PALETTE = {
  dark:  { bg: "#1A1614", dot: "#F2EDE4", dotGlow: "rgba(242, 237, 228, 0.18)" },
  light: { bg: "#F5EFE6", dot: "#2B2622", dotGlow: "rgba(43, 38, 34, 0.12)" },
};

const DOT_BRIGHTNESS_KEY = "realignmind-dot-brightness";

/** Fires Meta Pixel PageView on each route change so Facebook gets one event per "page" in the SPA. */
function MetaPixelPageView() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <LoopsExitPopup />
      <Routes>
      {/* Home route should be accessible always */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/how-it-works" element={<AboutEMDR />} />
      <Route path="/what-is-bilateral-stimulation" element={<WhatIsBilateralStimulation />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/confirmation" element={<Confirmation />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/login" element={<SignInSignUp />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/return-policy" element={<ReturnPolicy />} />
      <Route path="/emergency-support" element={<CrisisPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/account-setup" element={<AccountSetup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/demo" element={<PublicDemo />} />
      <Route path="/try" element={<PublicTrySession />} />
      <Route path="/technical-stack" element={<TechnicalStack />} />
      {/* Landing Pages for A/B Testing PPC Campaigns */}
      <Route path="/lp/a" element={<LandingPageA />} />
      <Route path="/lp/b" element={<LandingPageB />} />
      <Route path="/lp/stress" element={<StressLandingPage />} />
      <Route path="/lp/balance" element={<BalanceLandingPage />} />
      {/* Alternative URL paths for landing pages */}
      <Route path="/welcome" element={<LandingPageA />} />
      <Route path="/start" element={<LandingPageB />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute />}>
        {/* Include trailing "*" to support nested routes */}
        <Route path="/sessions/*" element={<SessionSelection />} />
        <Route path="/sessions/art" element={<ARTSession />} />
        <Route path="/sessions/unguided" element={<UnguidedSession />} />
        <Route path="/session-history" element={<SessionHistory />} />
        <Route path="/session-end" element={<SessionEnd />} />
        <Route path="/profile" element={<UserProfile />} />
      </Route>
      {/* Redirect any unspecified routes to home */}
      <Route path="*" element={<Home />} />
    </Routes>
    </>
  );
}

// Check if user prefers reduced motion
const getInitialDotSpeed = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return 2500; // Slowest speed (0.4 Hz) for users who prefer reduced motion
    }
  }
  return 1300; // Default speed (~0.77 Hz), ~30% slower than 1 Hz for gentler first experience
};

const getInitialDotBrightness = () => {
  if (typeof window === "undefined") return 1.0;
  const raw = window.localStorage.getItem(DOT_BRIGHTNESS_KEY);
  const parsed = raw === null ? NaN : parseFloat(raw);
  if (Number.isFinite(parsed) && parsed >= 0.3 && parsed <= 1.0) return parsed;
  return 1.0;
};

function AppInner() {
  const { effectiveTheme } = useSessionTheme();
  const palette = SESSION_PALETTE[effectiveTheme] || SESSION_PALETTE.dark;

  const { dotColor, bgColor, dotGlow } = useMemo(
    () => ({ dotColor: palette.dot, bgColor: palette.bg, dotGlow: palette.dotGlow }),
    [palette.dot, palette.bg, palette.dotGlow]
  );

  const [dotSize, setDotSize] = useState(55); // Middle of sidebar range (10–100)
  const [dotSpeed, setDotSpeed] = useState(getInitialDotSpeed);
  const [dotBrightness, setDotBrightnessState] = useState(getInitialDotBrightness);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const setDotBrightness = (value) => {
    const clamped = Math.max(0.3, Math.min(1.0, parseFloat(value) || 1.0));
    setDotBrightnessState(clamped);
    try {
      window.localStorage.setItem(DOT_BRIGHTNESS_KEY, String(clamped));
    } catch {
      // ignore
    }
  };

  // Fast dot for testing: __fastDotForTesting() or __fastDotForTesting(30). Apply any pending value from console.
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.__PENDING_FAST_DOT) {
        setDotSpeed(window.__PENDING_FAST_DOT);
        delete window.__PENDING_FAST_DOT;
      }
      window.__setDotSpeed = setDotSpeed;
      window.__fastDotForTesting = (ms) => setDotSpeed(typeof ms === "number" ? ms : 50);
    }
  }, []);
  const [shouldRun, setShouldRun] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [exitSession, setExitSession] = useState(false);
  const [soundFile, setSoundFile] = useState(DEFAULT_SOUND_FILE);
  const [mute, setMute] = useState(false);
  const [volume, setVolume] = useState(0.5); // 0 to 1

  const [sessionCuesEnabled, setSessionCuesEnabledState] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("sessionCuesEnabled") !== "false";
  });
  const setSessionCuesEnabled = (value) => {
    setSessionCuesEnabledState(value);
    localStorage.setItem("sessionCuesEnabled", value ? "true" : "false");
  };

  return (
    <DotContext.Provider
      value={{
        dotColor,
        bgColor,
        dotGlow,
        dotSize,
        setDotSize,
        dotSpeed,
        setDotSpeed,
        dotBrightness,
        setDotBrightness,
        sidebarVisible,
        setSidebarVisible,
        shouldRun,
        setShouldRun,
        isPaused,
        setIsPaused,
        exitSession,
        setExitSession,
        soundFile,
        setSoundFile,
        setMute,
        mute,
        volume,
        setVolume,
        sessionCuesEnabled,
        setSessionCuesEnabled,
      }}
    >
      <Router>
        <MetaPixelPageView />
        <Sidebar
          isVisible={sidebarVisible}
          closeSidebar={() => setSidebarVisible(false)}
          dotSize={dotSize}
          setDotSize={setDotSize}
          dotSpeed={dotSpeed}
          setDotSpeed={setDotSpeed}
          exitSession={exitSession}
          setExitSession={setExitSession}
        />
        <AppContent />
        <CookieConsentBar />
      </Router>
    </DotContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <SessionThemeProvider>
          <AppInner />
        </SessionThemeProvider>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
